import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Heart, Rocket, Trash2, Sparkles, ImagePlus, BadgeCheck, X, MessageCircle, Loader2, Flame, Clock,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 24;

// Pattern of grid spans for Instagram-Explore-style variety.
// Every 6th tile is a featured 2x2 block.
function spanClassFor(index, boosted) {
  // Featured tile every 6 items, or for boosted posts.
  if (boosted) return "col-span-2 row-span-2";
  const pos = index % 6;
  if (pos === 0) return "col-span-2 row-span-2";
  return "col-span-1 row-span-1";
}

function PostTile({ post, index, onOpen }) {
  const span = spanClassFor(index, post.boosted);
  return (
    <button
      onClick={() => onOpen(post)}
      className={`relative ${span} group overflow-hidden bg-[#0A0B0D] border-2 border-ink rounded-none focus:outline-none focus:ring-4 focus:ring-[#14C8B0]`}
      data-testid={`post-tile-${post.id}`}
      aria-label={post.title || post.author?.name}
    >
      {post.media_type === "video" ? (
        <video
          src={post.media_url}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <img
          src={post.media_url}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      )}

      {/* Hover gradient + stats */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="absolute bottom-0 inset-x-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-between text-white">
        <span className="flex items-center gap-1 text-sm font-display font-bold">
          <Heart className="w-4 h-4" strokeWidth={2.5} fill={post.liked_by_me ? "currentColor" : "none"} />
          {post.likes_count}
        </span>
        {post.media_type === "video" && (
          <span className="text-xs font-mono uppercase tracking-widest">▶</span>
        )}
      </div>

      {post.boosted && (
        <span className="absolute top-2 start-2 px-2 py-1 rounded-full bg-[#F9C349] border-2 border-ink shadow-nb-sm text-[10px] font-display font-black text-ink flex items-center gap-1">
          <Rocket className="w-3 h-3" strokeWidth={2.8} />
          BOOSTED
        </span>
      )}
      {!post.boosted && post.trend_likes_24h > 0 && (
        <span className="absolute top-2 start-2 px-2 py-1 rounded-full bg-[#FF7A6B] border-2 border-ink shadow-nb-sm text-[10px] font-display font-black text-white flex items-center gap-1">
          🔥 {post.trend_likes_24h}
        </span>
      )}
    </button>
  );
}

function PostModal({ post, currentUserId, onClose, onChanged, onDeleted }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  // Lock body scroll while modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const toggleLike = async () => {
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      onChanged({
        ...post,
        liked_by_me: data.liked,
        likes_count: post.likes_count + (data.liked ? 1 : -1),
      });
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };

  const boost = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/boost`);
      toast.success(t("postsPage.boostedToast", { n: data.coins_spent }));
      onChanged({ ...post, boosted: true });
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(t("postsPage.deleteConfirm"))) return;
    try {
      await api.delete(`/posts/${post.id}`);
      toast.success(t("postsPage.deletedToast"));
      onDeleted(post.id);
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" onClick={onClose} data-testid="post-modal">
      <div className="relative bg-white border-2 border-ink rounded-2xl shadow-nb max-w-4xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 end-3 z-10 w-9 h-9 rounded-full bg-white border-2 border-ink shadow-nb-sm flex items-center justify-center hover:-translate-y-0.5 transition-transform" data-testid="post-modal-close">
          <X className="w-5 h-5" strokeWidth={2.5} />
        </button>

        <div className="grid md:grid-cols-[1.2fr_1fr]">
          {/* Media */}
          <div className="bg-black flex items-center justify-center md:max-h-[92vh]">
            {post.media_type === "video" ? (
              <video src={post.media_url} controls autoPlay className="w-full max-h-[92vh] object-contain" />
            ) : (
              <img src={post.media_url} alt={post.title} className="w-full max-h-[92vh] object-contain" />
            )}
          </div>

          {/* Details */}
          <div className="p-5 sm:p-6 flex flex-col">
            <button
              onClick={() => nav(`/u/${post.author_id}`)}
              className="flex items-center gap-3 mb-4 group/author"
              data-testid="post-modal-author"
            >
              <div className="w-11 h-11 rounded-full bg-[#FFF1D6] ring-1 ring-black/5 border-2 border-ink flex items-center justify-center font-display font-bold overflow-hidden">
                {post.author?.avatar_url
                  ? <img src={post.author.avatar_url} alt="" className="w-full h-full object-cover" />
                  : post.author?.name?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0 text-start">
                <div className="font-display font-extrabold flex items-center gap-1 truncate group-hover/author:underline">
                  {post.author?.name}
                  {post.author?.verified && <BadgeCheck className="w-4 h-4 text-[#14C8B0]" strokeWidth={2.5} />}
                </div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#7C828D]">
                  {new Date(post.created_at).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                </div>
              </div>
            </button>

            {post.title && (
              <h2 className="font-display font-black text-2xl tracking-tight leading-tight mb-2">{post.title}</h2>
            )}
            {post.caption && (
              <p className="text-[15px] text-[#0F1115] whitespace-pre-wrap leading-relaxed mb-4">{post.caption}</p>
            )}
            {post.activity && (
              <button
                onClick={() => nav(`/activity/${post.activity_id}`)}
                className="tag-nb self-start mb-4"
                data-testid="post-modal-activity"
              >
                {t("postsPage.fromActivity")} <b className="ms-1">{post.activity.title}</b>
              </button>
            )}

            <div className="flex flex-wrap items-center gap-2 mt-auto pt-4 border-t-2 border-ink/10">
              <button
                onClick={toggleLike}
                className={`btn-nb !py-2 !px-3 ${post.liked_by_me ? "btn-nb-coral" : ""}`}
                data-testid={`like-${post.id}`}
              >
                <Heart className="w-4 h-4" strokeWidth={2.5} fill={post.liked_by_me ? "currentColor" : "none"} />
                <span>{post.likes_count}</span>
              </button>
              <button
                onClick={() => nav(`/chat/with/${post.author_id}`)}
                className="btn-nb !py-2 !px-3"
                data-testid={`message-${post.id}`}
              >
                <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
              </button>
              {post.author_id === currentUserId && !post.boosted && (
                <button onClick={boost} disabled={busy} className="btn-nb btn-nb-warn !py-2 !px-3" data-testid={`boost-post-${post.id}`}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" strokeWidth={2.5} />}
                  {t("postsPage.boost10")}
                </button>
              )}
              {post.boosted && (
                <span className="tag-nb bg-gradient-to-r from-[#F9C349] to-[#FFB347] text-[#4A3000] border-transparent">
                  <Rocket className="w-3 h-3" strokeWidth={2.5} /> {t("postsPage.boosted")}
                </span>
              )}
              {(post.author_id === currentUserId || (user && user.role === "admin" && (user.is_master || user.email === "mustafahaidari03@gmail.com" || (user.admin_permissions || []).includes("reports") || (user.admin_permissions || []).includes("users")))) && (
                <button onClick={del} className="btn-nb !p-2 ms-auto" data-testid={`post-delete-${post.id}`}>
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PostsFeed() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [openPost, setOpenPost] = useState(null);
  const [tab, setTab] = useState("new"); // "new" | "trending"

  const sentinelRef = useRef(null);

  const loadMore = useCallback(async (reset = false) => {
    if (!reset && (loadingMore || !hasMore)) return;
    if (reset) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), sort: tab });
      if (!reset && cursor) params.set("cursor", cursor);
      const { data } = await api.get(`/posts?${params}`);
      const items = data.items || [];
      setPosts((prev) => (reset ? items : [...prev, ...items]));
      setCursor(data.next_cursor);
      setHasMore(Boolean(data.next_cursor));
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [cursor, hasMore, loadingMore, tab]);

  // Initial load + reload on tab change
  useEffect(() => {
    setCursor(null);
    setHasMore(true);
    loadMore(true);
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll — observe sentinel near the bottom.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loading) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, loadMore]);

  const onPostChanged = (updated) => {
    setPosts((arr) => arr.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    setOpenPost((cur) => (cur && cur.id === updated.id ? { ...cur, ...updated } : cur));
  };

  const onPostDeleted = (id) => {
    setPosts((arr) => arr.filter((p) => p.id !== id));
    setOpenPost(null);
  };

  return (
    <div className="max-w-7xl mx-auto pt-4 pb-32">
      {/* Header */}
      <header className="px-4 sm:px-6 lg:px-8 mb-6 flex items-end justify-between gap-3">
        <div>
          <p className="text-xs font-display font-bold uppercase tracking-[0.3em] text-[#8A8F98]">
            {t("postsPage.moments")}
          </p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            {t("postsPage.fromThe")}{" "}
            <span className="bg-gradient-to-r from-[#0F9D8F] via-[#14C8B0] to-[#5EEAD4] bg-clip-text text-transparent">
              {t("postsPage.community")}
            </span>
          </h1>
          <p className="text-xs text-[#8A8F98] font-semibold mt-1" data-testid="moments-expiry-note">
            {t("postsPage.expiresNote")}
          </p>
        </div>
        <button onClick={() => nav("/post/new")} className="btn-nb btn-nb-primary shrink-0" data-testid="new-post-btn">
          <ImagePlus className="w-4 h-4" strokeWidth={2.5} /> {t("postsPage.post")}
        </button>
      </header>

      {/* Tabs: New | Trending */}
      <div className="px-4 sm:px-6 lg:px-8 mb-4 flex items-center gap-2" data-testid="feed-tabs">
        <button
          onClick={() => setTab("new")}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-ink font-display font-bold text-sm transition-all ${
            tab === "new" ? "bg-ink text-white shadow-nb-sm" : "bg-white hover:-translate-y-0.5"
          }`}
          data-testid="tab-new"
        >
          <Clock className="w-4 h-4" strokeWidth={2.5} />
          {t("postsPage.tabNew", { defaultValue: "New" })}
        </button>
        <button
          onClick={() => setTab("trending")}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-ink font-display font-bold text-sm transition-all ${
            tab === "trending" ? "bg-[#FF7A6B] text-white shadow-nb-sm" : "bg-white hover:-translate-y-0.5"
          }`}
          data-testid="tab-trending"
        >
          <Flame className="w-4 h-4" strokeWidth={2.5} />
          {t("postsPage.tabTrending", { defaultValue: "Trending" })}
          <span className="text-[10px] font-mono uppercase tracking-widest opacity-70">24h</span>
        </button>
      </div>

      {/* Loading skeleton */}
      {loading && posts.length === 0 && (
        <div
          className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 auto-rows-[120px] sm:auto-rows-[160px] md:auto-rows-[180px] gap-[2px]"
          data-testid="posts-skeleton"
        >
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className={`${i % 6 === 0 ? "col-span-2 row-span-2" : ""} bg-gradient-to-br from-[#E8E3D6] to-[#D7D2C5] animate-pulse`}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && posts.length === 0 && (
        <div className="mx-4 sm:mx-6 lg:mx-8 card-nb p-10 text-center" style={{ background: "linear-gradient(135deg, #ECE6FF, #D9CFFF)" }} data-testid="posts-empty">
          <Sparkles className="w-12 h-12 mx-auto mb-3" strokeWidth={2.25} />
          <h3 className="font-display font-extrabold text-2xl mb-1 tracking-tight">{t("postsPage.noMoments")}</h3>
          <p className="text-[#4A4F58] mb-4">{t("postsPage.shareFromAct")}</p>
          <button onClick={() => nav("/post/new")} className="btn-nb btn-nb-primary">
            <ImagePlus className="w-4 h-4" strokeWidth={2.5} /> {t("postsPage.createFirst")}
          </button>
        </div>
      )}

      {/* Instagram-Explore-style grid */}
      {posts.length > 0 && (
        <>
          <div
            className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 auto-rows-[120px] sm:auto-rows-[160px] md:auto-rows-[180px] gap-[2px]"
            data-testid="posts-grid"
          >
            {posts.map((post, i) => (
              <PostTile key={post.id} post={post} index={i} onOpen={setOpenPost} />
            ))}
          </div>

          {/* Infinite-scroll sentinel + loading indicator */}
          <div ref={sentinelRef} className="h-20 flex items-center justify-center mt-4">
            {loadingMore && <Loader2 className="w-6 h-6 animate-spin text-[#14C8B0]" strokeWidth={2.5} />}
            {!hasMore && (
              <p className="font-mono text-xs uppercase tracking-widest text-[#7C828D]">
                · {t("postsPage.endOfFeed", { defaultValue: "you're all caught up" })} ·
              </p>
            )}
          </div>
        </>
      )}

      {openPost && (
        <PostModal
          post={openPost}
          currentUserId={user?.id}
          onClose={() => setOpenPost(null)}
          onChanged={onPostChanged}
          onDeleted={onPostDeleted}
        />
      )}
    </div>
  );
}
